import { useRouteParams } from '@vueuse/router'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { SubmissionsOverview } from '~/types/Submission'
// import { useTestStore } from '~/stores/test'
import type { Test, TestFormat } from '~/types/Test'
import { testFormatMap } from '~/static/data/tests/testFormatOptions'
import { NotFoundError } from '~/errors/NotFoundError'
import {
  SseMessageType,
  type TestAttemptGradeStatusUpdateMessage,
  type TestLockUpdateMessage,
  type TestScoreDistributionUpdateMessage,
} from '~/types/SseMessage'
import { GradingStatus } from '~/types/TestAttempt'
import type { TestScoreDistribution } from '~/types/ScoreDistribution'
import { TestService } from '~/services/api/test/test'
import { TestSubmissionsService } from '~/services/api/test/test-submissions'

export const useTestSubmissionOverviewStore = defineStore(
  'testSubmissionOverview',
  () => {
    // SSE =========================================

    // SERVICES ==========================================
    const testService = new TestService()
    const testSubmissionsService = new TestSubmissionsService()
    const { addToast } = useToastNotifications()
    // const testStore = useTestStore()

    // META ==============================================
    const testId = useRouteParams<string>('testId')

    // TEST ================================================
    const test = ref<Test | null>(null)

    const testTitle = computed(() => {
      if (test.value === null) {
        return null
      }

      return test.value.title
    })

    const testFormat = computed<TestFormat | undefined>(() => {
      if (test.value === null) {
        return undefined
      }

      return test.value.format
    })

    const testFormatText = computed(() => {
      if (testFormat.value === undefined) {
        return null
      }

      return testFormatMap[testFormat.value]
    })

    async function populateTest(testId: string) {
      // await testStore.$ensureInitialised()
      // const data = testStore.getTestById(testId)
      // TODO: we need to reference an up to date test store
      const { data } = await testService.getTestById(testId, ['outcomes'])

      if (!data) {
        captureError(new NotFoundError(`Test with id ${testId} not found`))
        return
      }

      test.value = data
    }

    // OVERVIEW ============================================
    const submissionsOverview = ref<SubmissionsOverview | null>(null)

    async function populateSubmissionsOverview(testId: string) {
      const { data, error } =
        await testService.getTestSubmissionsOverview(testId)

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error fetching submissions overview',
          message: 'There was an error fetching the submissions overview',
          life: 5000,
        })
        return
      }

      submissionsOverview.value = data
    }

    // SCORE DISTRIBUTION =========================================
    const scoreDistribution = ref<TestScoreDistribution | null>(null)

    async function populateScoreDistribution(testId: string) {
      const { data, error } =
        await testSubmissionsService.getTestScoreDistribution(testId)

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error fetching score distribution',
          message: 'There was an error fetching the score distribution',
          life: 5000,
        })
        return
      }

      scoreDistribution.value = data
    }

    async function forceRefreshScoreDistribution() {
      if (!testId.value || typeof testId.value !== 'string') {
        return
      }

      const { data, error } =
        await testSubmissionsService.refreshTestScoreDistribution(testId.value)

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error refreshing score distribution',
          message: 'There was an error refreshing the score distribution',
          life: 5000,
        })
        return
      }

      scoreDistribution.value = data
    }

    addToWindowDevTools(
      'forceRefreshTestScoreDistribution',
      forceRefreshScoreDistribution,
    )

    function handleTestScoreDistributionUpdate(
      data: TestScoreDistributionUpdateMessage,
    ) {
      if (!scoreDistribution.value) return

      if (!data.data?.testId) {
        console.warn('Attempted to update score distribution with no testId')
        return
      }

      if (data.data.testId !== testId.value) return

      if (!data.data.lowerBound || !data.data.upperBound || !data.data.count) {
        console.warn(
          `Attempt to update score distribution with invalid data: ${JSON.stringify(
            data.data,
          )}`,
        )
        return
      }

      const binIndex = scoreDistribution.value.scoreDistribution.findIndex(
        (bin) =>
          bin.lowerBound === data.data.lowerBound &&
          bin.upperBound === data.data.upperBound,
      )

      if (binIndex === -1) {
        console.warn(
          `Attempted to update score distribution for bin that does not exist: ${data.data.lowerBound} - ${data.data.upperBound}`,
        )
        return
      }

      scoreDistribution.value.scoreDistribution[binIndex] = {
        lowerBound: data.data.lowerBound,
        upperBound: data.data.upperBound,
        count: data.data.count,
      }
    }

    // TEST LOCK ============================================
    function handleTestLockUpdate(data: TestLockUpdateMessage) {
      if (test.value?.id !== data.id) return

      test.value.lockedBy = data.data.lockedBy ?? null
      test.value.lockedAt = data.data.lockedAt ?? null
    }

    // SSE EVENT SUBSCRIPTIONS ====================================
    onEvent(
      SseMessageType.TEST_ATTEMPT_GRADE_STATUS_UPDATE,
      handleTestAttemptGradeStatusUpdate,
    )
    onEvent(
      SseMessageType.TEST_SCORE_DISTRIBUTION_UPDATE,
      handleTestScoreDistributionUpdate,
    )
    onEvent(SseMessageType.TEST_LOCK_UPDATE, handleTestLockUpdate)

    // STORE MANAGER =======================================
    function reset() {
      submissionsOverview.value = null
      scoreDistribution.value = null
      test.value = null
    }

    async function populateAll(testId: string) {
      await Promise.all([
        populateTest(testId),
        populateSubmissionsOverview(testId),
        populateScoreDistribution(testId),
      ])
    }

    function handleTestAttemptGradeStatusUpdate(
      data: TestAttemptGradeStatusUpdateMessage,
    ) {
      if (!testId.value || typeof testId.value !== 'string') {
        return
      }

      if (!data.data) return

      if (data.data.testId !== testId.value) return

      if (!data.data.status) return

      if (
        data.data.status === GradingStatus.Complete ||
        data.data.status === GradingStatus.Error
      ) {
        refreshDataDebounced()
      }
    }

    const refreshDataDebounced = useDebounceFn(
      async () => {
        await $refresh()
      },
      5000,
      { maxWait: 8000 },
    )

    const {
      $isInitialised,
      $isInitialising,
      $initialise,
      $ensureInitialised,
      $reset,
      $refresh,
    } = useStoreManager(
      computed(() => !!testId.value),

      async () => {
        reset()

        if (!testId.value || typeof testId.value !== 'string') {
          return false
        }

        await populateAll(testId.value)

        return true
      },

      reset,

      async () => {
        if (!testId.value || typeof testId.value !== 'string') {
          return
        }
        await populateAll(testId.value)
      },
    )

    return {
      $isInitialised,
      $isInitialising,
      $initialise,
      $ensureInitialised,
      $reset,
      $refresh,
      submissionsOverview,
      test,
      testTitle,
      testFormat,
      testFormatText,
      scoreDistribution,
      forceRefreshScoreDistribution,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useTestSubmissionOverviewStore, import.meta.hot),
  )
}
